import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Receipt, AlertTriangle, Check, Loader2 } from "lucide-react";
import { API, api as apiClient } from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Commercialista() {
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [insoluti, setInsoluti] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const loadInsoluti = async () => {
    try { const { data } = await apiClient.get("/fatture/insoluti"); setInsoluti(data); } catch {}
  };
  useEffect(() => { loadInsoluti(); }, []);

  const markPaid = async (id) => {
    try {
      await apiClient.patch(`/fatture/${id}/stato`, { stato: "pagata", data_pagamento: new Date().toISOString().slice(0, 10) });
      toast.success("Fattura segnata come pagata");
      loadInsoluti();
    } catch { toast.error("Errore"); }
  };

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/export/commercialista.csv?anno=${anno}&mese=${mese}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `commercialista-${anno}-${String(mese).padStart(2, "0")}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export scaricato");
    } catch (e) { toast.error("Errore export"); }
    finally { setDownloading(false); }
  };

  const mesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const scadute = insoluti.filter((f) => f.scaduta);
  const totInsoluti = insoluti.reduce((s, f) => s + (f.totale || 0), 0);
  const totScadute = scadute.reduce((s, f) => s + (f.totale || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Contabilità</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Export Commercialista</h1>
        <p className="text-muted-foreground mt-1">Esporta il registro IVA mensile e tieni sotto controllo gli insoluti.</p>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Anno</Label>
              <Input data-testid="exp-anno" type="number" value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="w-28" />
            </div>
            <div>
              <Label>Mese</Label>
              <Select value={String(mese)} onValueChange={(v) => setMese(Number(v))}>
                <SelectTrigger data-testid="exp-mese" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{mesi.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="exp-csv" onClick={downloadCSV} disabled={downloading} className="h-11">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Scarica CSV (Excel)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Il CSV contiene tutte le fatture del mese, calcolo IVA, stato pagamento e un riepilogo per il tuo commercialista.
          </p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="card-elevated">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale insoluti</div>
            <div className="text-3xl font-extrabold">{fmt(totInsoluti)}</div>
            <div className="text-xs text-muted-foreground mt-1">{insoluti.length} fatture da pagare</div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-destructive">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-destructive">Scadute</div>
            <div className="text-3xl font-extrabold text-destructive">{fmt(totScadute)}</div>
            <div className="text-xs text-muted-foreground mt-1">{scadute.length} fatture oltre la scadenza</div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-5 pb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h3 className="font-bold">Fatture insolute</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Scadenza</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Controparte</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Totale</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Stato</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {insoluti.map((f) => (
                <tr key={f.id} className={`border-t border-border ${f.scaduta ? "bg-destructive/5" : ""}`}>
                  <td className="px-4 py-3 font-medium">{f.data_scadenza || "—"}</td>
                  <td className="px-4 py-3">{f.tipo}</td>
                  <td className="px-4 py-3">{f.controparte}</td>
                  <td className="px-4 py-3 tabular-nums font-bold">{fmt(f.totale)}</td>
                  <td className="px-4 py-3">
                    {f.scaduta ? <span className="text-destructive text-xs font-bold">SCADUTA</span> : <span className="text-xs text-muted-foreground">da pagare</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`pay-${f.id}`} size="sm" variant="outline" onClick={() => markPaid(f.id)}>
                      <Check className="h-3 w-3 mr-1" /> Segna pagata
                    </Button>
                  </td>
                </tr>
              ))}
              {insoluti.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  <Receipt className="h-6 w-6 mx-auto mb-2 opacity-50" /> Nessun insoluto. Tutto sotto controllo!
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
